import React, { useRef, useState } from 'react'
import { Send, Sparkles, Paperclip, X, Image as ImageIcon } from 'lucide-react'
import GenerationCounter from './GenerationCounter'

interface PromptInputProps {
  value: string
  onChange: (value: string) => void
  onGenerate: () => void
  isGenerating: boolean
  disabled?: boolean
  uploadedImages?: File[]
  onImagesChange?: (images: File[]) => void
}

const PromptInput: React.FC<PromptInputProps> = ({
  value,
  onChange,
  onGenerate,
  isGenerating,
  disabled = false,
  uploadedImages = [],
  onImagesChange
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isGenerating && !disabled) {
      e.preventDefault()
      onGenerate()
    }
  }

  const handleFileSelect = (files: FileList | null) => {
    if (!files || !onImagesChange) return
    
    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    )
    
    if (imageFiles.length > 0) {
      const newImages = [...uploadedImages, ...imageFiles].slice(0, 3) // Max 3 images
      onImagesChange(newImages)
    }
  }

  const handleAttachClick = () => {
    if (disabled) return
    fileInputRef.current?.click()
  }

  const handleRemoveImage = (index: number) => {
    if (!onImagesChange) return
    const newImages = uploadedImages.filter((_, i) => i !== index)
    onImagesChange(newImages)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) setDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  const canSubmit = (value.trim() || uploadedImages.length > 0 || disabled) && !isGenerating && !disabled

  return (
    <div className="space-y-4">
      {/* Generation Counter */}
      <GenerationCounter variant="full" />
      
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Generate Feet Images</h2>
            <p className="text-sm text-gray-600">Describe what you want or upload reference images</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Image Upload Area */}
          {uploadedImages.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <ImageIcon className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Reference Images</span>
                <span className="text-xs text-gray-500">({uploadedImages.length}/3)</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {uploadedImages.map((file, index) => (
                  <div key={index} className="relative group">
                    <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text Input Area */}
          <div 
            className={`relative ${dragOver ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={disabled 
                ? "Sign up to continue generating images..." 
                : uploadedImages.length > 0
                ? "Describe how to modify or enhance your reference images... (optional)"
                : "Describe the feet you want to see, upload reference images, or use filters..."
              }
              disabled={disabled}
              className={`w-full p-4 pr-12 border-2 rounded-xl resize-none transition-colors ${
                disabled 
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
                  : dragOver
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 focus:border-primary-500 focus:outline-none'
              }`}
              rows={3}
              maxLength={500}
            />
            
            {/* Attachment Button */}
            <button
              onClick={handleAttachClick}
              disabled={disabled || uploadedImages.length >= 3}
              className={`absolute bottom-3 right-12 p-2 rounded-lg transition-colors ${
                disabled || uploadedImages.length >= 3
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
              }`}
              title={uploadedImages.length >= 3 ? 'Maximum 3 images allowed' : 'Upload reference image'}
            >
              <Paperclip className="w-4 h-4" />
            </button>

            <div className="absolute bottom-3 right-3 text-xs text-gray-400">
              {value.length}/500
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
          </div>

          {/* Drag and Drop Hint */}
          {dragOver && (
            <div className="absolute inset-0 bg-primary-50 bg-opacity-90 rounded-xl flex items-center justify-center border-2 border-dashed border-primary-500">
              <div className="text-center">
                <ImageIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <p className="text-primary-700 font-medium">Drop images here</p>
                <p className="text-primary-600 text-sm">Up to 3 reference images</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {disabled 
                ? "Free generations used up! Sign up for unlimited access." 
                : uploadedImages.length > 0
                ? "AI will use your images as reference for generation"
                : "Press Enter to generate, upload images, or use filters"
              }
            </div>
            
            <button
              onClick={onGenerate}
              disabled={!canSubmit}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                !canSubmit
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : disabled
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </>
              ) : disabled ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Sign Up to Continue</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Generate</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PromptInput 